'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { initPostHog, getPostHog } from '@/lib/analytics/posthog-client';
import { initWebVitals } from '@/lib/analytics/web-vitals';

// Initializes PostHog (+ Web Vitals → PostHog) on mount and captures a manual
// $pageview on every client-side route change. No-op when PostHog is disabled.
//
// User identification is intentionally NOT wired here — this base template has
// no auth provider. Once an auth component (e.g. Clerk) is installed, call
// identifyUser() from src/lib/analytics/identify.ts with the signed-in user.
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    initPostHog();
    try {
      initWebVitals();
    } catch {
      // swallow web-vitals init errors so they never break the app shell
    }
  }, []);

  useEffect(() => {
    if (!pathname) return;
    const client = getPostHog();
    if (!client) return;

    const query = searchParams?.toString();
    const url = query ? `${pathname}?${query}` : pathname;
    client.capture('$pageview', { $current_url: url });
  }, [pathname, searchParams]);

  return <>{children}</>;
}
